import type { JSX } from 'react'
import type { ITimeLine } from './TimeLine.h'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  InlinePrimaryBackSmallButton,
  InlinePrimaryForwardSmallButton,
} from 'atoms/Button/Templates'
import { getValueForBreakpoint } from 'utils/helpers'
import { useScrollbarAppearance } from 'utils/hooks'
import { TimeLineIndicator } from './components/TimeLineIndicator'
import { TimeLineSection } from './components/TimeLineSection'
import { ONE_DAY_MS, SCALE_BREAKPOINTS } from './constants'
import { useTimeLine, useUpdateStartTime } from './hooks'
import { TimeLineBackground } from './TimeLine.h'
import { TimeLineSC } from './TimeLine.styled'

const dayIncludes =
  (time: number) =>
  ({ stamp }: { stamp: number }) =>
    stamp <= time && time < stamp + ONE_DAY_MS

export const TimeLine = ({
  now,
  backgroundColor = TimeLineBackground.Default,
  startTime,
  scaleDivision,
  onUpdateTime,
  setTimeBlockWidth,
  updateStep,
  updateShowProgressLine,
  setSections,
  coveredDays,
  onUpdateStartTime,
  formatTime,
}: ITimeLine): JSX.Element => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const [sectionWidth, setSectionWidth] = useState(0)
  const [indicatorWidth, setIndicatorWidth] = useState(0)
  const [titleWidth, setTitleWidth] = useState<number | undefined>()

  const [step, setStep] = useState(0)

  const { hasScrollbar } = useScrollbarAppearance()

  const valueSection = getValueForBreakpoint(
    document.documentElement.clientWidth ?? 0,
    SCALE_BREAKPOINTS
  )

  useEffect(() => {
    onUpdateTime?.({
      scaleDivisionMs: scaleDivision,
      durationMs: scaleDivision * valueSection,
    })
  }, [onUpdateTime, scaleDivision, valueSection])

  const { loadNextPeriod, loadPrevPeriod } = useUpdateStartTime({
    now,
    startTime,
    scaleDivision,
    valueSection,
    onUpdateStartTime,
  })

  const shouldShowTimeLineIndicator =
    startTime <= now && now <= startTime + scaleDivision

  const canLoadPrevPeriod = useMemo(() => {
    if (shouldShowTimeLineIndicator) return false // do not allow to go back in time
    if (coveredDays && coveredDays.length) {
      const newStartTime = startTime - scaleDivision * valueSection
      return coveredDays.findIndex(dayIncludes(newStartTime)) !== -1
    }
  }, [startTime, coveredDays, now, scaleDivision, valueSection])

  const canLoadNextPeriod = useMemo(() => {
    if (coveredDays && coveredDays.length) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const newStartTime = startTime + scaleDivision * valueSection
      if (newStartTime >= today.getTime() && newStartTime < now - scaleDivision)
        return false // do not allow to go to the today from the past
      return coveredDays.findIndex(dayIncludes(newStartTime)) !== -1
    }
  }, [startTime, coveredDays, now, scaleDivision, valueSection])

  const updateWidth = useCallback(() => {
    const ref = sectionRef.current
    if (ref) {
      setSectionWidth(ref.clientWidth)
      const timelineStartTimeLabelNode = ref.children[0]?.children[1]!
      const isTimelineStartTimeLabel = Boolean(
        timelineStartTimeLabelNode.textContent?.length
      )

      setTitleWidth((prev) =>
        isTimelineStartTimeLabel
          ? timelineStartTimeLabelNode.getBoundingClientRect().width
          : prev
      )
    }

    if (indicatorRef.current)
      setIndicatorWidth(indicatorRef.current.clientWidth)
  }, [sectionRef.current, indicatorRef.current])

  const indicatorTitle = useMemo(
    () => formatTime(now),
    [now, startTime, formatTime]
  )

  useLayoutEffect(() => {
    updateWidth()
  }, [updateWidth, hasScrollbar])

  useLayoutEffect(() => {
    setStep(sectionWidth / new Date(scaleDivision).getMinutes())
  }, [sectionWidth, scaleDivision])

  useLayoutEffect(() => {
    if (setTimeBlockWidth) setTimeBlockWidth(sectionWidth)
    if (setSections) setSections(valueSection)
  }, [valueSection, sectionWidth])

  useLayoutEffect(() => {
    updateStep?.(step)
  }, [step, updateStep])

  useLayoutEffect(() => {
    updateShowProgressLine?.(shouldShowTimeLineIndicator)
  }, [shouldShowTimeLineIndicator, updateShowProgressLine])

  const { titleTime, positionIndicator } = useTimeLine({
    now,
    startTime,
    step,
    indicatorWidth,
    titleWidth,
    scaleDivision,
    formatTime,
  })

  const valueSectionToArray = [...new Array(valueSection)]
  const RenderTime = (): JSX.Element => (
    <>
      {valueSectionToArray.map((_, i) => {
        return <TimeLineSection ref={sectionRef} key={i} title={titleTime(i)} />
      })}
    </>
  )

  return (
    <TimeLineSC
      backgroundColor={backgroundColor}
      valueSection={valueSection}
      positionIndicator={positionIndicator()}
      sectionWidth={sectionWidth}
      indicatorWidth={indicatorWidth}
    >
      {canLoadPrevPeriod && (
        <InlinePrimaryBackSmallButton onClick={loadPrevPeriod} />
      )}
      <RenderTime />
      {shouldShowTimeLineIndicator && (
        <TimeLineIndicator ref={indicatorRef} title={indicatorTitle} />
      )}
      {canLoadNextPeriod && (
        <InlinePrimaryForwardSmallButton onClick={loadNextPeriod} />
      )}
    </TimeLineSC>
  )
}
